import { TooltipProvider } from "@/components/ui/tooltip"
import { minutesToLabel } from "@/app/booking-calendar/lib/siteTime"
import { cellKey, cellState, type EditorState, GRID_WEEKDAYS, isLocked, weekdayLabel } from "../lib/editorModel"
import type { CellMarker } from "../lib/overrideOverlay"
import type { WeekColumn } from "../lib/weekModel"
import { EditorCell } from "./EditorCell"

/**
 * The Mon–Fri grid: one row per slot of the plan's own length inside its own window, both from the
 * GET (AB#780/783), never the calendar's constants — an outreach site's 15-minute rows and its
 * single break are the server's to say.
 *
 * With `columns` (the dated week, AB#803) each column is headed by its date ("Mon 10/19") and its
 * cells are named by it; without, by the weekday alone, as the weekly default always is.
 *
 * With `markers` (the override overlay, AB#803) the cells an override changed carry its marker and
 * open its detail through `onOpenMarker`; every other cell is drawn exactly as without.
 *
 * With `pending` (AB#806) the cells inside the window of an override being made are drawn as pending,
 * named for its kind; the rest, and the grid without it, are unchanged.
 */
export function AvailabilityGrid({
  state,
  editable,
  onCycle,
  columns,
  markers,
  onOpenMarker,
  label,
  pending,
}: {
  state: EditorState
  editable: boolean
  onCycle: (weekday: number, startMinutes: number) => void
  columns?: readonly WeekColumn[]
  /** By cellKey(weekday, start): the cells an override changed. */
  markers?: Readonly<Record<string, CellMarker>>
  onOpenMarker?: (marker: CellMarker) => void
  /** The grid's accessible name before its slot length; "Weekly availability" by default. */
  label?: string
  /** The override being made: the cells its window covers, by cellKey, and its kind's name. */
  pending?: { cells: ReadonlySet<string>; label: string }
}) {
  const { frame } = state
  const days = columns
    ?? GRID_WEEKDAYS.map(weekday => ({ weekday, header: weekdayLabel(weekday), label: weekdayLabel(weekday) }))

  const grid = (
    <div className="overflow-x-auto">
      <div
        className="grid min-w-[640px] gap-1"
        style={{ gridTemplateColumns: `5.5rem repeat(${days.length}, minmax(0, 1fr))` }}
        role="grid"
        aria-label={`${label ?? "Weekly availability"}, ${frame.slotMinutes}-minute slots`}
      >
        <div role="columnheader" />
        {days.map(({ weekday, header }) => (
          <div key={weekday} role="columnheader" className="text-center text-xs font-semibold text-muted-foreground">
            {header}
          </div>
        ))}
        {frame.rows.map(start => (
          <div key={start} role="row" className="contents">
            <div role="rowheader" className="flex h-7 items-center justify-end pr-2 text-[11px] text-muted-foreground tabular-nums">
              {minutesToLabel(start)}
            </div>
            {days.map(({ weekday, label: dayName }) => {
              const key = cellKey(weekday, start)
              const marker = markers?.[key]
              return (
                <div key={weekday} role="gridcell">
                  <EditorCell
                    state={cellState(state, weekday, start)}
                    locked={isLocked(frame, weekday, start)}
                    editable={editable}
                    label={`${dayName} ${minutesToLabel(start)}`}
                    onCycle={() => onCycle(weekday, start)}
                    marker={marker
                      ? { kinds: marker.kinds, description: marker.description, onOpen: () => onOpenMarker?.(marker) }
                      : undefined}
                    pending={pending?.cells.has(key) ? pending.label : undefined}
                  />
                </div>
              )
            })}
          </div>
        ))}
      </div>
    </div>
  )

  // The provider draws nothing: an overlaid grid's markup is the plain grid's plus its markers.
  return markers ? <TooltipProvider delayDuration={150}>{grid}</TooltipProvider> : grid
}
